/**
 * AuthProvider — session state for SonAuto Admin.
 *
 * Holds the `GET /api/v1/auth/me` payload and exposes it to the whole tree:
 * the user, the tenant, the reachable branches, the active branch and the
 * effective permission set (both for the active branch and per branch).
 *
 * Flow:
 *   1. On mount, if a refresh token exists we call `/auth/me`. The API client
 *      transparently refreshes the access token on the resulting 401, so a
 *      returning user never sees a login screen just because their tab was
 *      closed overnight.
 *   2. `login()` posts credentials, stores the tokens, then bootstraps.
 *   3. `switchBranch()` posts to `/auth/switch-branch` and re-bootstraps,
 *      because the effective permission set is branch-scoped — a shift leader
 *      may hold `order.cancel` in branch A and not in branch B.
 *   4. `onUnauthenticated` from the client clears everything and the router
 *      falls through to the login page.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { PermissionProvider } from '@sonauto/ui';
import type { LoginRequest, MeResponse, SessionBranch } from '@sonauto/api-client';
import { api, onUnauthenticated } from '../lib/api.js';
import { clearTokens, hasSession, rememberTenantCode, setTokens } from '../lib/tokenStorage.js';

export interface AuthState {
  readonly status: 'loading' | 'authenticated' | 'anonymous';
  readonly me: MeResponse | null;
  readonly error: string | null;
}

export interface AuthContextValue extends AuthState {
  readonly branches: readonly SessionBranch[];
  readonly activeBranchId: string | null;
  readonly activeBranch: SessionBranch | null;
  login: (input: Omit<LoginRequest, 'app'>) => Promise<void>;
  logout: () => Promise<void>;
  switchBranch: (branchId: string) => Promise<void>;
  /** Re-fetch `/auth/me`, e.g. after the user's roles were changed. */
  reload: () => Promise<void>;
  has: (permission: string, branchId?: string | null) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const [state, setState] = useState<AuthState>({
    status: hasSession() ? 'loading' : 'anonymous',
    me: null,
    error: null,
  });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const bootstrap = useCallback(async (): Promise<void> => {
    try {
      const me = await api.auth.me();
      if (mounted.current) setState({ status: 'authenticated', me, error: null });
    } catch (error) {
      clearTokens();
      if (mounted.current) {
        setState({
          status: 'anonymous',
          me: null,
          error: error instanceof Error ? error.message : 'Không tải được phiên đăng nhập.',
        });
      }
    }
  }, []);

  // Restore an existing session on first load.
  useEffect(() => {
    if (!hasSession()) return;
    void bootstrap();
  }, [bootstrap]);

  // The API client tells us when the refresh token is gone or rejected.
  useEffect(
    () =>
      onUnauthenticated(() => {
        if (mounted.current) setState({ status: 'anonymous', me: null, error: null });
      }),
    [],
  );

  const login = useCallback<AuthContextValue['login']>(
    async (input) => {
      const result = await api.auth.login({ ...input, app: 'ADMIN' });
      setTokens({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAt: result.expiresAt,
      });
      rememberTenantCode(input.tenantCode);
      // `mustChangePassword` / `mfaRequired` are surfaced by the login screen
      // from this same response; the session is still valid either way.
      await bootstrap();
    },
    [bootstrap],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.auth.logout();
    } catch {
      // A failed logout must still clear the device. The server-side session
      // expires on its own, and leaving tokens behind is the worse outcome.
    }
    clearTokens();
    if (mounted.current) setState({ status: 'anonymous', me: null, error: null });
  }, []);

  const switchBranch = useCallback(
    async (branchId: string): Promise<void> => {
      await api.auth.switchBranch(branchId);
      // Re-bootstrap rather than patching state: `permissions` is computed for
      // the ACTIVE branch, so the whole payload changes.
      await bootstrap();
    },
    [bootstrap],
  );

  const permissionSets = useMemo(() => {
    const active = new Set(state.me?.permissions ?? []);
    const byBranch: Record<string, ReadonlySet<string>> = {};
    for (const [branchId, codes] of Object.entries(state.me?.permissionsByBranch ?? {})) {
      byBranch[branchId] = new Set(codes);
    }
    return { active, byBranch };
  }, [state.me]);

  const has = useCallback<AuthContextValue['has']>(
    (permission, branchId) => {
      if (branchId === undefined || branchId === null) return permissionSets.active.has(permission);
      return (permissionSets.byBranch[branchId] ?? new Set<string>()).has(permission);
    },
    [permissionSets],
  );

  const activeBranchId = state.me?.session.activeBranchId ?? null;
  const branches = state.me?.branches ?? [];

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      branches,
      activeBranchId,
      activeBranch: branches.find((b) => b.id === activeBranchId) ?? null,
      login,
      logout,
      switchBranch,
      reload: bootstrap,
      has,
    }),
    [state, branches, activeBranchId, login, logout, switchBranch, bootstrap, has],
  );

  return (
    <AuthContext.Provider value={value}>
      {/* The UI package's PermissionGate reads from this context. */}
      <PermissionProvider
        value={{
          permissions: permissionSets.active,
          permissionsByBranch: permissionSets.byBranch,
          activeBranchId,
        }}
      >
        {children}
      </PermissionProvider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) throw new Error('useAuth phải được dùng bên trong <AuthProvider>.');
  return ctx;
}

/** Convenience for screens that only need the permission check. */
export function useHasPermission(): (permission: string, branchId?: string | null) => boolean {
  return useAuth().has;
}
